import { useCallback, useEffect, useMemo, useState } from "react";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2, Send, ClipboardList, Banknote } from "lucide-react";
import { Attachments } from "@/components/Attachments";

const TONE = { pending: "secondary", accepted: "default", rejected: "destructive", withdrawn: "outline" };
const EMPTY_LINE = { product_id: "", qty: 1, discount_level: 0, above_list_price_per_bag: 0 };
let lineSeq = 0;
const newLine = () => ({ ...EMPTY_LINE, _k: ++lineSeq });

export default function AgentSubmit() {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [drafts, setDrafts] = useState(null);
  const [form, setForm] = useState({ customer_id: "", new_customer: false, new_customer_name: "", new_customer_phone: "", note: "", cod_paid: false, lines: [newLine()] });
  const [est, setEst] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadDrafts = useCallback(() => api.get("/agent/drafts").then((r) => setDrafts(r.data)), []);
  useEffect(() => {
    api.get("/agent/plan").then((r) => setProducts(r.data.products || []));
    api.get("/agent/customers/pick").then((r) => setCustomers(r.data));
    loadDrafts();
  }, [loadDrafts]);

  const validLines = useMemo(() => form.lines.filter((l) => l.product_id && Number(l.qty) > 0), [form.lines]);
  useEffect(() => {
    if (!validLines.length) { setEst(null); return; }
    const t = setTimeout(() => {
      api.post("/agent/drafts/estimate", { lines: validLines.map(({ _k, ...l }) => ({ ...l, qty: Number(l.qty), discount_level: Number(l.discount_level), above_list_price_per_bag: Number(l.above_list_price_per_bag || 0) })) })
        .then((r) => setEst(r.data)).catch(() => setEst(null));
    }, 250);
    return () => clearTimeout(t);
  }, [validLines]);

  const setLine = (i, patch) => setForm({ ...form, lines: form.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/agent/drafts", {
        customer_id: form.new_customer ? null : form.customer_id || null,
        new_customer_name: form.new_customer ? form.new_customer_name : null,
        new_customer_phone: form.new_customer ? form.new_customer_phone : null,
        note: form.note, cod_paid: form.cod_paid,
        lines: validLines.map(({ _k, ...l }) => ({ ...l, qty: Number(l.qty), discount_level: Number(l.discount_level), above_list_price_per_bag: Number(l.above_list_price_per_bag || 0) })),
      });
      toast.success("Sale submitted - the office will verify and invoice it");
      setForm({ customer_id: "", new_customer: false, new_customer_name: "", new_customer_phone: "", note: "", cod_paid: false, lines: [newLine()] });
      loadDrafts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  const withdraw = async (d) => {
    try {
      await api.post(`/agent/drafts/${d.id}/withdraw`);
      toast.success("Submission withdrawn");
      loadDrafts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const canSubmit = validLines.length > 0 && (form.new_customer ? form.new_customer_name.trim() : form.customer_id);

  return (
    <div className="space-y-4">
      <h1 className="font-display text-2xl font-bold tracking-tight">Submit a sale</h1>
      <p className="text-sm text-muted-foreground -mt-2">Record what you sold in the field. The office verifies it and issues the invoice - nothing is final until then.</p>

      <Card className="p-4 space-y-3" data-testid="submit-form">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Customer</Label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground"><Switch checked={form.new_customer} onCheckedChange={(v) => setForm({ ...form, new_customer: v, customer_id: "" })} data-testid="new-customer-switch" /> New customer</label>
        </div>
        {form.new_customer ? (
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Shop / customer name" value={form.new_customer_name} onChange={(e) => setForm({ ...form, new_customer_name: e.target.value })} data-testid="new-customer-name-input" />
            <Input placeholder="Phone" value={form.new_customer_phone} onChange={(e) => setForm({ ...form, new_customer_phone: e.target.value })} data-testid="new-customer-phone-input" />
          </div>
        ) : (
          <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
            <SelectTrigger data-testid="submit-customer-select"><SelectValue placeholder="Pick one of your customers" /></SelectTrigger>
            <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        )}

        <Label className="text-xs">Products</Label>
        {form.lines.map((l, i) => (
          <div key={l._k} className="rounded-lg border border-border/60 p-2.5 space-y-2" data-testid={`submit-line-${i}`}>
            <div className="flex gap-2">
              <Select value={l.product_id} onValueChange={(v) => setLine(i, { product_id: v })}>
                <SelectTrigger data-testid={`submit-product-select-${i}`}><SelectValue placeholder="Product" /></SelectTrigger>
                <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} {p.pack}</SelectItem>)}</SelectContent>
              </Select>
              {form.lines.length > 1 && <Button variant="ghost" size="icon" onClick={() => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) })} data-testid={`remove-line-${i}`}><Trash2 className="w-4 h-4" /></Button>}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <F label="Bags"><Input type="number" min={1} value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} data-testid={`submit-qty-${i}`} /></F>
              <F label="Discount">
                <Select value={String(l.discount_level)} onValueChange={(v) => setLine(i, { discount_level: Number(v) })}>
                  <SelectTrigger data-testid={`submit-discount-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{Array.from({ length: 11 }, (_, k) => <SelectItem key={k} value={String(k)}>{k}%{k > 5 ? " (approval)" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Above list /bag"><Input type="number" min={0} step="0.5" value={l.above_list_price_per_bag} onChange={(e) => setLine(i, { above_list_price_per_bag: e.target.value })} data-testid={`submit-premium-${i}`} /></F>
            </div>
          </div>
        ))}
        <Button variant="outline" size="sm" className="w-full" onClick={() => setForm({ ...form, lines: [...form.lines, newLine()] })} data-testid="add-line-button"><Plus className="w-4 h-4 mr-1" /> Add product</Button>

        <label className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5 text-sm">
          <span className="flex items-center gap-2"><Banknote className="w-4 h-4 text-primary" /> Customer paid cash on delivery</span>
          <Switch checked={form.cod_paid} onCheckedChange={(v) => setForm({ ...form, cod_paid: v })} data-testid="cod-switch" />
        </label>
        <Textarea rows={2} placeholder="Note for the office (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="submit-note-input" />

        {est && (
          <div className="rounded-lg bg-secondary/50 p-3 text-sm flex items-center justify-between" data-testid="submit-estimate">
            <span>{est.bags} bags · {money(est.sales)}</span>
            <span className="text-primary font-medium">≈ {money(est.commission)} commission{est.max_discount > 5 ? " (needs approval)" : ""}</span>
          </div>
        )}
        <Button className="w-full h-11" disabled={!canSubmit || saving} onClick={submit} data-testid="submit-sale-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Submit for verification</>}</Button>
      </Card>

      <h2 className="font-display text-lg font-semibold flex items-center gap-2 pt-2"><ClipboardList className="w-4 h-4 text-primary" /> My submissions</h2>
      {!drafts ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        : drafts.items.length === 0 ? <p className="text-sm text-muted-foreground">Nothing submitted yet.</p>
          : drafts.items.map((d) => (
            <Card key={d.id} className="p-3.5" data-testid={`my-draft-${d.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-sm">{d.customer_name}{d.new_customer && <span className="text-xs text-muted-foreground"> · new</span>}</p>
                  <p className="text-xs text-muted-foreground">{d.date} · {d.estimate.bags} bags · {money(d.estimate.sales)}{d.cod_paid ? " · COD" : ""}</p>
                </div>
                <Badge variant={TONE[d.status]} className="capitalize">{d.status}</Badge>
              </div>
              {d.status === "accepted" && <p className="text-xs text-primary mt-1">Invoice {d.invoice_no}</p>}
              {d.status === "rejected" && d.reason && <p className="text-xs text-destructive mt-1">{d.reason}</p>}
              {d.status === "pending" && (
                <>
                  <div className="mt-2"><Attachments entityType="draft" entityId={d.id} kinds={["photo"]} compact /></div>
                  <Button size="sm" variant="ghost" className="mt-1 h-7 px-2 text-xs" onClick={() => withdraw(d)} data-testid={`withdraw-draft-${d.id}`}>Withdraw</Button>
                </>
              )}
            </Card>
          ))}
    </div>
  );
}

function F({ label, children }) {
  return <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">{label}</Label>{children}</div>;
}
